<template>
  <div v-frag class="fadeIn">
    <CardIeaAppMain>
      <v-container fluid class="pa-4">
        <!-- HEADER -->
        <v-row align="center" class="mb-6">
          <v-col cols="12">
            <h1 class="text-h4 font-weight-bold bce-text-gradient">Rendir Exámenes</h1>
            <p class="grey--text text--darken-1">Accede a tus evaluaciones activas, tests de práctica y consulta tus calificaciones en tiempo real.</p>
          </v-col>
        </v-row>

        <div v-if="loading" class="text-center pa-10">
          <v-progress-circular indeterminate color="#E50050" size="48"></v-progress-circular>
          <div class="mt-4 grey--text">Cargando exámenes...</div>
        </div>

        <div v-else>
          <!-- DESKTOP VIEW -->
          <div v-if="$vuetify.breakpoint.mdAndUp">
            <v-card class="rounded-xl overflow-hidden elevation-3">
              <v-simple-table class="bce-premium-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left font-weight-bold grey--text text--darken-2">Materia / Examen</th>
                      <th class="text-center font-weight-bold grey--text text--darken-2">Tipo</th>
                      <th class="text-center font-weight-bold grey--text text--darken-2">Disponibilidad</th>
                      <th class="text-center font-weight-bold grey--text text--darken-2" style="width: 180px;">Acción</th>
                      <th class="text-center font-weight-bold grey--text text--darken-2" style="width: 100px;">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(test, keyTable) in data" :key="keyTable" class="bce-tr-hover">
                      <td class="py-4">
                        <div class="font-weight-bold bce-red-text">{{ test.nombreMateria }}</div>
                        <div class="text-caption grey--text">{{ test.titulo }}</div>
                      </td>
                      <td class="text-center">
                        <v-chip color="#f5f5f5" small class="font-weight-bold grey--text text--darken-3">
                          {{ test.tipo_examen || "Práctica" }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <div v-if="test.tipo != 'Practica'" class="text-caption">
                          <div class="font-weight-medium">{{ test.fecha_test_desde }} - {{ test.fecha_test_hasta }}</div>
                          <div class="grey--text">{{ formatTime(test.hora_inicio) }} a {{ formatTime(test.hora_final) }}</div>
                        </div>
                        <v-chip v-else small label color="success" outlined>Acceso Libre</v-chip>
                      </td>
                      <td class="text-center">
                        <div v-if="test.tipo == 'Practica' || (test.testPresentado == false && puedeRendirExamen(test))">
                          <v-btn @click="linkTest(test)" color="#E50050" dark depressed class="rounded-lg text-none px-4" small>
                            Rendir Ahora
                          </v-btn>
                        </div>
                        <v-chip v-else-if="!puedeRendirExamen(test) && !test.testPresentado" color="grey lighten-2" small label class="font-weight-bold">
                          Cerrado
                        </v-chip>
                        <v-chip v-else color="success" dark small label class="font-weight-bold">
                          Presentado
                        </v-chip>
                        
                        <v-btn v-if="test.url_meet" :href="test.url_meet" target="_blank" icon color="#1976D2" class="ml-2" title="Formulario Externo">
                          <v-icon small>mdi-open-in-new</v-icon>
                        </v-btn>
                      </td>
                      <td class="text-center">
                        <v-avatar v-if="test.mostrar_nota" :color="test.nota >= 4 ? 'success' : 'error'" size="32" class="white--text font-weight-bold">
                          {{ test.nota }}
                        </v-avatar>
                        <span v-else class="grey--text">-</span>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </div>

          <!-- MOBILE VIEW -->
          <div v-else>
            <v-card v-for="(test, keyTable) in data" :key="keyTable" class="mb-4 rounded-xl elevation-2 overflow-hidden bce-card-mobile" outlined>
              <div class="pa-4 bce-header-mini white--text" :class="test.nota >= 4 ? 'bg-success' : 'bce-header-red'">
                <div class="d-flex justify-space-between align-center">
                  <div class="text-subtitle-2 font-weight-bold">{{ test.nombreMateria }}</div>
                  <div v-if="test.mostrar_nota" class="text-h6 font-weight-black">{{ test.nota }}</div>
                </div>
              </div>
              <v-card-text class="pa-4">
                <div class="text-body-2 font-weight-bold mb-1">{{ test.titulo }}</div>
                <div class="d-flex align-center mb-3">
                  <v-chip x-small color="grey lighten-3" class="mr-2">{{ test.tipo_examen || "Práctica" }}</v-chip>
                  <span v-if="test.tipo != 'Practica'" class="text-caption grey--text">
                    {{ test.fecha_test_desde }} ({{ formatTime(test.hora_inicio) }})
                  </span>
                </div>

                <div class="mt-4">
                  <v-btn
                    v-if="test.tipo == 'Practica' || (test.testPresentado == false && puedeRendirExamen(test))"
                    @click="linkTest(test)"
                    color="#E50050"
                    block
                    dark
                    depressed
                    class="rounded-lg text-none"
                  >
                    Ingresar al Test
                  </v-btn>
                  <v-alert v-else-if="!puedeRendirExamen(test) && !test.testPresentado" type="error" dense outlined class="mb-0 text-center py-1">
                    Examen Cerrado
                  </v-alert>
                  <v-alert v-else type="success" dense outlined class="mb-0 text-center py-1">
                    Examen Entregado
                  </v-alert>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- EMPTY STATE -->
          <v-alert
            v-if="data.length === 0"
            border="left"
            colored-border
            color="grey darken-1"
            elevation="2"
            class="rounded-xl mt-4"
          >
            No se encontraron exámenes activos o prácticas disponibles en este momento.
          </v-alert>
        </div>
      </v-container>
    </CardIeaAppMain>
  </div>
</template>

<script>
import CardIeaAppMain from "../../components/CardIeaAppMain.vue";
import moment from "moment";

export default {
  name: "RendirExamenes",
  components: { CardIeaAppMain },
  data() { 
    return { 
      data: [], 
      loading: true 
    } 
  },
  mounted() { 
    this.examenes(); 
  },
  methods: {
    async examenes() {
      this.loading = true;
      try {
        const res = await this.$API.get({ url: "examenes", data: {} });
        this.data = res || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    formatTime(time) {
      if (!time) return "";
      const date = new Date(`1970-01-01T${time}Z`);
      return date.toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }).replace(":00", ""); 
    },
    puedeRendirExamen(examen) {
      const fechaDesde = moment(examen.fecha_test_desde + " " + examen.hora_inicio).utcOffset(-180);
      const fechaHasta = moment(examen.fecha_test_hasta + " " + examen.hora_final).utcOffset(-180);
      const now = moment().utcOffset(-180);
      return now.isBetween(fechaDesde, fechaHasta, null, "[]");
    },
    linkTest(test) {
       let params = test.tipo == 'Practica' ? { test: test.id, examen: test.test_Id } : { test: test.test_Id, examen: test.id };
       this.$router.push({ name: 'examen', params });
    }
  }
}
</script>

<style scoped>
.bce-text-gradient {
  background: linear-gradient(45deg, #1a237e 30%, #E50050 90%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.bce-red-text {
  color: #E50050;
}

.bce-premium-table thead th {
  background-color: #f8f9fa !important;
  height: 50px !important;
  text-transform: uppercase;
  font-size: 11px !important;
  letter-spacing: 0.5px;
}

.bce-tr-hover:hover {
  background-color: #fffafa;
}

.bce-header-red {
  background: linear-gradient(135deg, #E50050 0%, #b3003e 100%);
}

.bce-header-mini {
  padding: 8px 16px;
}

.bce-card-mobile {
  border: 1px solid rgba(0,0,0,0.05) !important;
}

.fadeIn { 
  animation: fadeIn 0.4s ease-in-out; 
}

@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
}
</style>
